export default customElements.define('w-criteria-textbox', class CriteriaTextbox extends wealthify.CustomElementBaseClass {

    constructor () {
        const styleSelector = 'c-criteria-textbox';
        const config = {
            styleSelector: styleSelector,
            templateHTML: `
            <div class="${styleSelector}__container" data-root>
                <div class="${styleSelector}__list-container u-display__hidden" data-listcontainer>
                    <p class="${styleSelector}__list-title">Your <span data-fieldname></span> must contain:</p>
                    <ul class="${styleSelector}__list" data-list></ul>
                </div>
                <w-textbox required="true" data-input></w-textbox>
            </div>`,
            hasChildren: true,
            cacheDom: [ 'data-input', 'data-listcontainer', 'data-fieldname', 'data-list' ],
            defaultSlotContents: {button: ''}
        };

        super(config);

        this.onConnectedCallback = () => {
            const isPrivateAndOnDesktop = this.isprivate && !wealthify.user.isOnMobileDevice();

            this.classList.add(this.styleSelector);

            this._updateValues();
            this._toggleTextboxValueVisibility(isPrivateAndOnDesktop);

            this.dom.input.addEventListener('valuechange', this._validateAndUpdateValues.bind(this));
            this.dom.input.addEventListener('visibilitytoggled', this._toggleTextboxValueVisibility.bind(this), true);

            this.dom.input.addEventListener('focus', this._toggleListVisibility.bind(this), true);
            this.dom.input.addEventListener('blur', this._toggleListVisibility.bind(this), true);
        };

    }

    static get requiredProperties () {
        return [ 'regex', 'description' ];
    }

    static get validPositions () {
        return [ 'bottom', 'top' ];
    }

    static get observedProps() {
        return [
            new wealthify.Prop('autocompletion', {default: 'off'}),
            new wealthify.Prop('criteria', {
                type: wealthify.propTypes.Array(),
                validators: [
                    wealthify.propValidators.ArrayObjectContainsKeys(this.requiredProperties)
                ]
            }),
            new wealthify.Prop('key'),
            new wealthify.Prop('isprivate', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            new wealthify.Prop('position', {
                default: 'top',
                type: wealthify.propTypes.ArrayItem(this.validPositions)
            }),
            new wealthify.Prop('typename', {default: 'password'}),
            new wealthify.Prop('value', {default: ''}),
            wealthify.props.Disabled(),
        ];
    }

    get propCallbacks() {
        return {
            autocompletion: () => {
                this.dom.input.autocompletion = this.autocompletion;
            },
            criteria: () => {
                const fragment = document.createDocumentFragment();

                this.items = [];
                this.dom.list.innerHTML = '';

                this.criteria.forEach((criteria, index) => {
                    this.items[index] = {};
                    const listItem = this.items[index].listItem = document.createElement('li');
                    const ariaSpan = this.items[index].ariaSpan = document.createElement('span');

                    listItem.classList.add(`${this.styleSelector}__list-item`);
                    listItem.setAttribute('aria-live', 'polite');
                    listItem.setAttribute('aria-atomic', true);

                    ariaSpan.classList.add('u-display__hidden');
                    this._updateSingleCriteriaValidity(criteria.regex, index);

                    listItem.appendChild(ariaSpan);
                    listItem.insertAdjacentHTML('beforeend', criteria.description);

                    fragment.appendChild(listItem);
                });

                this.dom.list.appendChild(fragment);
            },
            key: () => {
                this.dom.input.key = this.key;
            },
            isprivate: () => {
                if (this.isprivate) {
                    const button = document.createElement('w-widget-button');
                    const userOnMobile = wealthify.user.isOnMobileDevice();

                    button.icon = 'eye';
                    button.iconactivated = 'eye-struck';
                    button.type = 'uncircled';
                    button.activated = userOnMobile;
                    button.setAttribute('data-slot', 'button');
                    button.onclickprop = this._togglePasswordVisibility;

                    this.dom.input.appendChild(button);
                    this.dom.input.addEventListener('keydown', this._restrictAngleBrackets.bind(this));
                    this.dom.input.addEventListener('paste', this._restrictAngleBrackets.bind(this));

                    if (userOnMobile) {
                        button.onclickprop();
                    }
                }
            },
            position: () => {
                this.constructor.validPositions.forEach((position) => {
                    this.dom.listcontainer.classList.remove(`${this.styleSelector}__list-container--position-${position}`);
                });

                this.dom.listcontainer.classList.add(`${this.styleSelector}__list-container--position-${this.position}`);
            },
            typename: () => {
                this.dom.fieldname.innerHTML = this.typename;
            },
            value: () => {
                this.dom.input.value = this.value;
            }
        };
    }

    _togglePasswordVisibility () {
        const event = document.createEvent('Event');

        this.activated = !this.activated;
        event.data = {hidden: !this.activated};

        event.initEvent('visibilitytoggled', true, true);
        this.dispatchEvent(event);
    }

    _restrictAngleBrackets (event) {
        const containsAngleBrackets = this._checkEventContainsAngleBrackets(event);

        if (containsAngleBrackets) {
            event.preventDefault();
        }
    }

    _checkEventContainsAngleBrackets (event) {
        const data = event.clipboardData ? event.clipboardData.getData('text/plain') : event.key;
        const regexAngleBrackets = /[<>]+/;

        return new RegExp(regexAngleBrackets).test(data);
    }

    _validateAndUpdateValues () {
        this._updateAllCriteriaValidity();
        this._updateValues();
    }

    _toggleTextboxValueVisibility (value) {
        const isHidden = value.data ? value.data.hidden : value;
        const type = isHidden ? 'password' : 'text';

        this.dom.input.type = type;
    }

    _toggleListVisibility () {
        this.dom.listcontainer.classList.toggle('u-display__hidden');
    }

    _updateAllCriteriaValidity () {
        if (!this.criteria) {
            return;
        }

        this.criteria.forEach((criteria, index) => {
            this._updateSingleCriteriaValidity(criteria.regex, index);
        });
    }

    _updateSingleCriteriaValidity (regex, index) {
        const valid = this._getStringCriteriaValidity(regex);
        const validClassListAction = valid ? 'add' : 'remove';
        const invalidClassListAction = valid ? 'remove' : 'add';

        this.items[index].ariaSpan.innerHTML  = valid ? 'Passed:' : 'Not passed:';

        this.items[index].listItem.classList[validClassListAction](`${this.styleSelector}__list-item--valid`);
        this.items[index].listItem.classList[invalidClassListAction](`${this.styleSelector}__list-item--invalid`);
    }

    _getStringCriteriaValidity (regex) {
        const inputValue = this.dom.input.value || '';

        return this._convertStringToRegex(regex).test(inputValue);
    }

    _convertStringToRegex (regex) {
        try {
            return new RegExp(regex);
        } catch (error) {
            return wealthify.error.throwError(this.tagName, `Unable to create RegExp from value ${regex}`);
        }
    }

    _updateValues () {
        if (!this.criteria) {
            return;
        }

        this.value = this.dom.input.value;
        this.valid = this.criteria.every((criteria) => this._getStringCriteriaValidity(criteria.regex));

        this.dom.input.validoverride = this.valid;
    }

});